import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import {
  CircleDashedIcon,
  LightningIcon,
  RobotIcon,
  UserIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import type {
  GetTaskAutomationResponse,
  TaskAutomationItemResponse,
} from '@erp/rpc';
import { cn } from '@/lib/utils';
import { useTaskAutomation } from 'src/api/rpc/hooks/tasks/useTaskAutomation';

/**
 * Compact derived automation score for a typed task: the two rates (autonomy
 * + intervention) plus one icon per checklist item. Everything is recomputed
 * server-side from audit history — nothing persisted. Renders nothing for
 * untyped / no-checklist tasks.
 */
export function TaskAutomationCard({
  taskId,
  typeNameByKey,
}: {
  taskId: string;
  typeNameByKey: Map<string, string>;
}) {
  const { data: automation } = useTaskAutomation(taskId);
  if (!automation) return null;
  return (
    <TaskAutomationCardView
      automation={automation}
      typeNameByKey={typeNameByKey}
    />
  );
}

/** Display-only body, split out so tests can render assembled data directly. */
export function TaskAutomationCardView({
  automation,
  typeNameByKey,
}: {
  automation: GetTaskAutomationResponse;
  typeNameByKey: Map<string, string>;
}) {
  if (automation.items.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-border bg-white px-3 py-2"
      data-testid="task-automation-card"
    >
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500">
        <LightningIcon className="size-3.5" />
        <T context="heading for the derived task automation score block">
          Automation
        </T>
      </span>

      <RateStat
        label={
          <T context="share of checklist steps closed without a human">
            Autonomy
          </T>
        }
        rate={automation.autonomyRate}
        testId="automation-autonomy-rate"
      />
      <RateStat
        label={
          <T context="share of automated steps that needed a human anyway">
            Intervention
          </T>
        }
        rate={automation.interventionRate}
        testId="automation-intervention-rate"
        invertColor
      />

      <div className="flex items-center gap-1">
        {automation.items.map((item) => (
          <ItemIcon
            key={item.typeKey}
            item={item}
            label={typeNameByKey.get(item.typeKey) ?? item.typeKey}
          />
        ))}
      </div>

      {automation.isFullyAutonomous && (
        <span className="rounded-full border border-[oklch(0.82_0.1_150)] bg-[oklch(0.96_0.04_150)] px-2 py-0.5 text-[11px] font-medium text-[oklch(0.4_0.1_150)]">
          <T context="badge, every checklist step ran without a human">
            Fully autonomous
          </T>
        </span>
      )}
    </div>
  );
}

function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

function RateStat({
  label,
  rate,
  testId,
  invertColor = false,
}: {
  label: ReactNode;
  rate: number | null;
  testId: string;
  invertColor?: boolean;
}) {
  // Autonomy is good when high; intervention is good when low.
  const good = rate !== null && (invertColor ? rate === 0 : rate >= 0.75);
  const bad = rate !== null && (invertColor ? rate >= 0.5 : rate < 0.25);
  return (
    <span className="inline-flex items-baseline gap-1 text-xs text-stone-700">
      {label}
      <span
        data-testid={testId}
        className={cn(
          'font-semibold tabular-nums',
          rate === null && 'text-stone-400',
          good && 'text-[oklch(0.4_0.1_150)]',
          bad && 'text-[oklch(0.45_0.15_25)]',
        )}
      >
        {formatRate(rate)}
      </span>
    </span>
  );
}

type ItemVisual = {
  className: string;
  Icon: typeof CircleDashedIcon;
  weight?: 'bold';
};

function itemClosedStateLabel(
  closedByKind: TaskAutomationItemResponse['closedByKind'],
  labels: {
    notClosed: string;
    closedByPerson: string;
    closedAutomatically: string;
  },
): string {
  if (closedByKind === 'none') return labels.notClosed;
  if (closedByKind === 'user') return labels.closedByPerson;
  return labels.closedAutomatically;
}

function itemVisual(item: TaskAutomationItemResponse): ItemVisual {
  if (item.intervened) {
    return {
      className:
        'border-[oklch(0.85_0.1_70)] bg-[oklch(0.97_0.04_80)] text-[oklch(0.5_0.12_60)]',
      Icon: WarningCircleIcon,
      weight: 'bold',
    };
  }
  if (item.closedByKind === 'none') {
    return {
      className: 'border-stone-200 bg-stone-50 text-stone-400',
      Icon: CircleDashedIcon,
    };
  }
  if (item.closedByKind === 'user') {
    return {
      className:
        'border-[oklch(0.86_0.07_240)] bg-[oklch(0.96_0.03_240)] text-[oklch(0.42_0.13_250)]',
      Icon: UserIcon,
      weight: 'bold',
    };
  }
  return {
    className:
      'border-[oklch(0.82_0.1_150)] bg-[oklch(0.96_0.04_150)] text-[oklch(0.4_0.1_150)]',
    Icon: RobotIcon,
    weight: 'bold',
  };
}

function ItemIcon({
  item,
  label,
}: {
  item: TaskAutomationItemResponse;
  label: string;
}) {
  const gt = useGT();
  const stateLabel = itemClosedStateLabel(item.closedByKind, {
    notClosed: gt('not closed yet', {
      context: 'automation checklist item state',
    }),
    closedByPerson: gt('closed by a person', {
      context: 'automation checklist item state',
    }),
    closedAutomatically: gt('closed automatically', {
      context: 'automation checklist item state',
    }),
  });
  const intervenedSuffix = item.intervened
    ? gt(' — needed human intervention', {
        context: 'automation checklist item state suffix',
      })
    : '';
  const visual = itemVisual(item);
  const Icon = visual.Icon;

  return (
    <span
      title={`${label}: ${stateLabel}${intervenedSuffix}`}
      data-testid={`automation-item-${item.typeKey}`}
      data-closed-by={item.closedByKind}
      data-intervened={item.intervened}
      className={cn(
        'inline-flex size-5 items-center justify-center rounded-full border',
        visual.className,
      )}
    >
      <Icon className="size-3.5" weight={visual.weight} />
    </span>
  );
}
